import _ from 'lodash';
import classnames from 'classnames';
import Dotdotdot from 'react-dotdotdot';
import PureRenderMixin from 'react-addons-pure-render-mixin';
import React from 'react';
import PropTypes from 'prop-types';
import { Trans } from 'react-i18next';

import Agent from 'models/agent';
import ChatIconOutline from 'components/lib/icons/chat_icon_outline';
import { isApiInitiator } from 'models/communicator';
import connect from 'components/lib/connect';
import Conversation from 'models/conversation';
import ConversationIcon from 'components/lib/conversation_icon';
import ConversationItem from 'models/conversation_item';
import ConversationItemPreview from 'components/lib/conversation_item_preview';
import ConversationItemType from 'models/conversation_item_type';
import { MessageChannelType } from 'models/conversation_message';
import { CustomerActivityType } from 'models/customer_activity';
import { faTwitter, faWhatsapp, faInstagram } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getTopicNameFromItem } from 'components/lib/topic_name';
import { isVoiceAICallItem } from 'scripts/application/actions/helpers/voiceai_call_helpers';
import RelativeDateTime from 'components/lib/date/relative_date_time';
import RoutingGroup from 'models/routing_group';
import ShortList from 'components/common/short_list';
import { StyledIconAPI } from 'components/customer/conversation_history/conversation_items_v2/metadata/api_metadata';
import TicketIcon from 'components/lib/icons/ticket_icon';
import VoiceStrokeIcon from 'components/common/icons/stroke/voice-stroke';
import i18n from 'scripts/i18n/i18n';

const { CHAT_MESSAGE, CONVERSATION_MESSAGE, CUSTOMER_ACTIVITY, PHONE_CALL, TWITTER, VOICE_AI_MESSAGE } =
  ConversationItemType;
const baseClass = 'customerConversationCard-conversationInfo';

export class ConversationCardComponent extends React.Component {
  constructor(props) {
    super(props);
    this.shouldComponentUpdate = PureRenderMixin.shouldComponentUpdate.bind(this);
  }

  render() {
    return <div className={baseClass}>{this.props.conversation ? <Body {...this.props} /> : <Empty />}</div>;
  }
}

const ConversationCardConnector = connect(mapStateToProps)(ConversationCardComponent);
export default ConversationCardConnector;

ConversationCardComponent.propTypes = {
  assignedAgent: PropTypes.instanceOf(Agent),
  assignedGroup: PropTypes.instanceOf(RoutingGroup),
  conversation: PropTypes.instanceOf(Conversation),
  conversationItem: PropTypes.instanceOf(ConversationItem),
  customChannelNames: PropTypes.object,
  hasDraft: PropTypes.bool,
  taskCount: PropTypes.number,
};

function mapStateToProps({ getProvider }, props) {
  const customChannels = getProvider('customChannels').findAll();
  const customChannelNames = {};
  customChannels.forEach(channel => {
    customChannelNames[channel.id] = channel.name;
  });

  return {
    customChannelNames,
    ...props,
  };
}

export function Empty() {
  return <div className={cn('empty')}>{i18n.t('No previous conversations')}</div>;
}

export function Body({
  assignedAgent,
  assignedGroup,
  conversation,
  conversationItem,
  customChannelNames,
  hasDraft,
  taskCount,
  topics,
}) {
  return (
    <div className={cn('body')}>
      <div className={cn('topRow')}>
        <Title
          conversationItem={conversationItem}
          customChannelNames={customChannelNames}
          hasDraft={hasDraft}
          topics={topics}
        />
        <Sla conversation={conversation} conversationItem={conversationItem} />
        <Status conversation={conversation} />
        <LiveCall conversationItem={conversationItem} />
        <Icon conversationItem={conversationItem} />
      </div>
      <Excerpt conversation={conversation} conversationItem={conversationItem} />
      <Metadata
        assignedAgent={assignedAgent}
        assignedGroup={assignedGroup}
        createdAt={conversation && conversation.createdAt}
        taskCount={taskCount}
        updatedAt={conversation.updatedAt}
      />
    </div>
  );
}

Body.propTypes = ConversationCardComponent.propTypes;

export function Title({ conversationItem, customChannelNames, hasDraft, topics }) {
  return (
    <div className={cn('title', 'multipleTopics')}>
      <Dotdotdot clamp={1}>
        <div className={cn('draft-topic-wrapper')}>
          <HasDraft hasDraft={hasDraft} />
          <TitleContent conversationItem={conversationItem} customChannelNames={customChannelNames} topics={topics} />
        </div>
      </Dotdotdot>
    </div>
  );
}

export function HasDraft({ hasDraft }) {
  if (!hasDraft) {
    return null;
  }
  return (
    <div className={cn('hasDraft')}>
      {i18n.t('Draft')} <span className={cn('hasDraftDash')}>-</span>
    </div>
  );
}

export function TitleContent({ topics, conversationItem, customChannelNames }) {
  let defaultTitle = getTopicNameFromItem(conversationItem, customChannelNames);
  let titlePlaceholder = decorateTitleWithIcon(defaultTitle, conversationItem);

  return (
    <div className={cn('titleContent', 'multipleTopics')}>
      <ShortList className={cn('multipleTopics', 'titleContent')} placeholder={titlePlaceholder} values={topics} />
    </div>
  );
}

function decorateTitleWithIcon(defaultTitle, conversationItem) {
  const initiator = _.get(conversationItem, 'initiator');
  if (isApiInitiator(initiator) && !isVoiceAICallItem(conversationItem)) {
    return (
      <span className={cn('title')}>
        <span className={cn('title-api')}>
          <StyledIconAPI className={cn('title-api-icon')}>APP</StyledIconAPI>
        </span>
        {defaultTitle}
      </span>
    );
  }

  return defaultTitle;
}

export function Sla({ className, conversation, conversationItem }) {
  if (conversationItem && conversationItem.hasActivePhoneCall()) {
    return null;
  }

  const dueAt = conversation && conversation.isOpen() && conversation.dueAt;
  if (dueAt) {
    let danger = conversation.isOverdue() || conversation.isNearlyDue();
    return (
      <div className={classnames(cn('sla', { 'sla-danger': danger }), className)}>
        <Trans components={{ time: <RelativeDateTime timestamp={dueAt} /> }} i18nKey="Due <time></time>" />
      </div>
    );
  }

  return null;
}

export function Icon({ conversationItem }) {
  if (!conversationItem) {
    return null;
  }
  let conversationType = conversationItem.contentType();
  if (conversationType === ConversationItemType.MESSAGE_AUTOMATION_SUMMARY) {
    conversationType = conversationItem.content.getChannelType();
  }

  switch (conversationType) {
    case CHAT_MESSAGE:
      return <ChatIconOutline className="customerConversationCard-conversationInfo-chatIcon" />;
    case CONVERSATION_MESSAGE:
      return <MessageIcon conversationItem={conversationItem} />;
    case TWITTER:
      return <ChatIcon conversationItem={conversationItem} />;
    case PHONE_CALL:
      return <PhoneIcon conversationItem={conversationItem} />;
    case CUSTOMER_ACTIVITY:
      return <CustomerActivityIcon conversationItem={conversationItem} />;
    case VOICE_AI_MESSAGE:
      return <VoiceStrokeIcon className="customerConversationCard-conversationInfo-automatedCallIcon" />;
  }

  return (
    <div className={cn('icon')}>
      <ConversationIcon type={conversationType} />
    </div>
  );
}

export function ChatIcon({ conversationItem }) {
  const conversationType = conversationItem.contentType();

  return (
    <div className={cn('icon', 'chatIcon')}>
      <ConversationIcon iconClassName="fa fa-lg fa-fw" type={conversationType} />
    </div>
  );
}

export function CustomerActivityIcon({ conversationItem }) {
  const conversationType = conversationItem.contentType();

  if (conversationItem.content.activityType === CustomerActivityType.ISSUE) {
    return <TicketIcon className={cn('ticketIcon')} />;
  }
  return (
    <div className={cn('icon')}>
      <ConversationIcon subType={conversationItem.content.activityType} type={conversationType} />
    </div>
  );
}

export function MessageIcon({ conversationItem }) {
  let channel = conversationItem.content.getChannelType();
  switch (channel) {
    case MessageChannelType.WHATSAPP:
      return <FontAwesomeIcon className="customerConversationCard-conversationInfo-messageIcon" icon={faWhatsapp} />;
    case MessageChannelType.TWITTER:
      return <FontAwesomeIcon className="customerConversationCard-conversationInfo-messageIcon" icon={faTwitter} />;
    case MessageChannelType.INSTAGRAM_COMMENT:
    case MessageChannelType.INSTAGRAM_DIRECT:
      return <FontAwesomeIcon className="customerConversationCard-conversationInfo-messageIcon" icon={faInstagram} />;
    default:
      return (
        <div className={cn('icon')}>
          <ConversationIcon type={channel} />
        </div>
      );
  }
}

export function PhoneIcon({ conversationItem }) {
  let isLive = false;
  let isWaiting = false;
  if (conversationItem.contentType() === ConversationItemType.PHONE_CALL) {
    const call = conversationItem.content;
    isLive = call.isLive() || call.isOutgoing();
    isWaiting = call.isWaiting();
  }

  return (
    <div className={cn('icon', 'phoneIcon', { isLive, isWaiting })}>
      <ConversationIcon type={ConversationItemType.PHONE_CALL} />
    </div>
  );
}

export function LiveCall({ conversationItem }) {
  if (!conversationItem || !conversationItem.hasLivePhoneCall()) {
    return null;
  }
  return <div className={cn('liveCall', 'isLive')}>{i18n.t('Live')}</div>;
}

export function Excerpt({ conversationItem, conversation }) {
  let hasTopic = conversation?.topicIds?.length > 0;
  if (!conversationItem) {
    return null;
  }
  return (
    <div className={cn('excerpt')}>
      <Dotdotdot clamp={3}>
        <ConversationItemPreview conversationItem={conversationItem} hasTopic={hasTopic} />
      </Dotdotdot>
    </div>
  );
}

export function Metadata({ createdAt, assignedGroup, assignedAgent, taskCount, updatedAt }) {
  return (
    <div className={cn('metadata')}>
      <span className={cn('metadata-updatedAt')}>
        <UpdatedAt updatedAt={updatedAt} />,
      </span>
      {assignedGroup && <span className={cn('metadata-group')}> {assignedGroup.name},</span>}
      {assignedAgent && (
        <span className={cn('metadata-agent')}>
          {' '}
          <Trans values={{ name: assignedAgent.name }}>Assigned to {'{{name}}'},</Trans>
        </span>
      )}
      <span className={cn('metadata-created')}>
        {' '}
        <Trans components={{ time: <RelativeDateTime timestamp={createdAt} /> }} i18nKey="Created <time></time>" />
      </span>
      {taskCount > 0 && (
        <span className={cn('metadata-taskCount')}>
          {' '}
          <Trans count={taskCount} i18nKey="{{count}} Task" values={{ count: taskCount }} />
        </span>
      )}
    </div>
  );
}

export function UpdatedAt({ updatedAt }) {
  return (
    <div className={cn('updatedAt')}>
      {'Updated '}
      <RelativeDateTime timestamp={updatedAt} />
    </div>
  );
}

export function Status({ conversation }) {
  if (!conversation) {
    return null;
  }
  return <div className={cn('status')}>{conversation.getInboxStatus()}</div>;
}

// like classnames, but prepends the base class to the result
function cn(...args) {
  let res = classnames(...args);
  return res
    .split(' ')
    .map(c => `${baseClass}-${c}`)
    .join(' ');
}
